import { getApartmentUnits } from '@/api/properties';
import { getResidents } from '@/api/residents';
import { User } from '@/types/users';

export interface DynamicResidentTemplateRow {
  apartmentUnitId: string;
  apartmentUnitNumber: string;
  blockId: string;
  blockName: string;
  residentName: string;
  phoneNumber: string;
  email: string;
  type: string;
  status: string;
  moveInDate: string;
  notes: string;
}

/**
 * Generates a dynamic CSV template for residents based on apartment units in the workspace
 * @param firestoreUser - The authenticated user with workspace info
 * @param language - The language for the template (en, es, pt)
 * @returns Promise<string> - The CSV content as a string
 */
export async function generateResidentsTemplate(
  firestoreUser: User,
  language: 'en' | 'es' | 'pt' = 'en'
): Promise<string> {
  try {
    // Fetch all apartment units for the workspace
    const { apartmentUnits } = await getApartmentUnits({
      searchTerm: '',
      pageSize: 1000, // Get all units
      currentPage: 1,
      firestoreUser
    });

    // Fetch all residents to check which units already have residents
    const { residents } = await getResidents({
      searchTerm: '',
      pageSize: 1000, // Get all residents
      page: 1,
      firestoreUser
    });

    // Create a set of unit IDs that already have residents
    const unitsWithResidents = new Set(
      residents.map(resident => resident.apartmentUnit?.id).filter(Boolean)
    );

    // Filter out apartment units that already have residents
    const availableUnits = apartmentUnits.filter(unit => !unitsWithResidents.has(unit.id));

    if (availableUnits.length === 0) {
      throw new Error('No available apartment units found. All units already have residents.');
    }

    // Generate CSV headers based on language
    const headers = getHeadersForLanguage(language);
    
    // Generate CSV rows with apartment unit data pre-populated
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const rows = availableUnits.map(unit => {
      const apartmentUnitBlock = `${unit.number} - ${unit.block?.name || ''}`;
      return [
        apartmentUnitBlock, // apartmentUnitBlock (combined field)
        unit.number, // apartmentUnitNumber
        unit.block?.name || '', // blockName
        '', // residentName (empty for user to fill)
        '', // phoneNumber (empty for user to fill)
        '', // email (empty for user to fill)
        unit.id, // apartmentUnitId
        unit.block?.id || '', // blockId
        'owner', // type (default to owner)
        'pending', // status (default to pending)
        today, // moveInDate (pre-populated with today's date)
        '' // notes (empty for user to fill)
      ].map(field => `"${field}"`).join(',');
    });

    // Combine headers and rows
    const csvContent = [headers, ...rows].join('\n');
    
    return csvContent;
  } catch (error) {
    console.error('Error generating residents template:', error);
    throw new Error('Failed to generate residents template');
  }
}

/**
 * Gets the CSV headers based on the language
 */
function getHeadersForLanguage(language: 'en' | 'es' | 'pt'): string {
  const headerMappings = {
    en: [
      'apartmentUnitBlock',
      'apartmentUnitNumber', 
      'blockName',
      'residentName',
      'phoneNumber',
      'email',
      'apartmentUnitId',
      'blockId',
      'type',
      'status',
      'moveInDate',
      'notes'
    ],
    es: [
      'apartmentUnitBlock',
      'apartmentUnitNumber',
      'blockName',
      'residentName',
      'phoneNumber',
      'email',
      'apartmentUnitId',
      'blockId', 
      'type',
      'status',
      'moveInDate',
      'notes'
    ],
    pt: [
      'apartmentUnitBlock',
      'apartmentUnitNumber',
      'blockName', 
      'residentName',
      'phoneNumber',
      'email',
      'apartmentUnitId',
      'blockId',
      'type',
      'status',
      'moveInDate',
      'notes'
    ]
  };

  return headerMappings[language].map(header => `"${header}"`).join(',');
}

/**
 * Downloads a dynamically generated CSV template
 * @param firestoreUser - The authenticated user
 * @param language - The language for the template
 * @param filename - The filename for the download
 */
export async function downloadResidentsTemplate(
  firestoreUser: User,
  language: 'en' | 'es' | 'pt' = 'en',
  filename: string = 'residents_template.csv'
): Promise<void> {
  try {
    const csvContent = await generateResidentsTemplate(firestoreUser, language);
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error downloading residents template:', error);
    throw error;
  }
}
